(ns arthur.ui.tools
  "The tools: a strip down the left of the stage, an options bar across the top
  of it, and their keys.

  PHOTOSHOP'S ARRANGEMENT, which Illustrator and Flash share: one tool at a
  time, picked from a vertical strip or by its letter, and a bar above the
  canvas holding that tool's options — the brush's size, the pen's point count.
  The palette sits in the strip under the tools. See `events/ui` for what each
  tool does to the selection."
  (:require [arthur.events.ui :as ui]
            [arthur.subs.ui :as sub]
            [arthur.subs.render :as render]
            [arthur.ui.layout :as layout]
            [arthur.ui.palette :as palette]
            [re-frame.core :as rf]))

(def ^:private kit
  [{:tool :select :key "v" :name "Select"
    :tip "click to select, drag to move · double-click a shape to edit its points"
    :icon [:path {:d "M4 2 L4 14 L7 11 L9.5 16 L11.5 15 L9 10 L13 10 Z"}]}
   {:tool :pen :key "p" :name "Pen"
    :tip "click to place points · click the first to close · on a selected shape, click an edge to add a point, click a point and press Delete (or ⌥-click it) to remove it"
    :icon [:<> [:path {:d "M9 1 L14 8 L11.5 13 L6.5 13 L4 8 Z"}]
           [:path.cut {:d "M9 2 V8"}]
           [:rect {:x 6.5 :y 14 :width 5 :height 2}]]}
   {:tool :brush :key "b" :name "Brush"
    :tip "paint, and each piece becomes a polygon when you let go"
    :icon [:<> [:path {:d "M15 1.5 L16.5 3 L9 10.5 L7.5 9 Z"}]
           [:path {:d "M6.5 10 C4 10 3 11.5 3 13.5 C3 15 1.5 16 1.5 16 C5 16 8 15 8 11.5 Z"}]]}
   {:tool :eraser :key "e" :name "Eraser"
    :tip "cuts the shapes of the colour you start on, in its symbol · ⌥ cuts every colour"
    :icon [:<> [:path {:d "M10 2.5 L16 8.5 L10 14.5 L5.5 14.5 L2 11 Z"}]
           [:path.cut {:d "M6 6.5 L12 12.5"}]]}])

(defn toolbox []
  (let [tool @(rf/subscribe [::sub/tool])]
    [:aside.toolbox
     [:div.tools
      (doall
       (for [{t :tool label :name :keys [key tip icon]} kit]
         ^{:key t}
         [:button {:class (str "tool tool-" (name t) (when (= t tool) " on"))
                   :title (str label " (" (.toUpperCase key) ") — " tip)
                   :on-click #(rf/dispatch [::ui/set-tool t])}
          [:svg {:view-box "0 0 18 18" :width 18 :height 18} icon]]))]
     [palette/grid]
     [palette/assets]]))

(defn- size-control []
  (let [size @(rf/subscribe [::sub/brush])]
    [:label.option {:title "[ and ]"} "size"
     [:input {:type "range" :min 1 :max 64 :value size
              :on-change #(rf/dispatch [::ui/brush-size (js/parseInt (.. % -target -value))])}]
     [:span.value (str size "px")]]))

(defn- fit-control
  "How closely a stroke's polygon follows what was painted: every point of the
  painted edge within so many pixels of it. Closer is more points, and only
  where the shape bends."
  [fit event]
  [:label.option {:title "how far the polygon may stray from what you painted — closer is more points, only where it bends"}
   "fit within"
   [:input {:type "range" :min 0.5 :max 8 :step 0.5 :value fit
            :on-change #(rf/dispatch [event (js/parseFloat (.. % -target -value))])}]
   [:span.value (str fit "px")]])

(defn options []
  (let [tool  @(rf/subscribe [::sub/tool])
        draft @(rf/subscribe [::sub/draft])
        selections @(rf/subscribe [::sub/selections])
        {:keys [name tip]} (some #(when (= tool (:tool %)) %) kit)]
    [:div.options-bar
     [:strong.tool-name name]
     (case tool
       :pen (if (seq draft)
              [:<>
               [:span.dim (str (quot (count draft) 2) " points")]
               [:button {:disabled (< (count draft) 6) :title "Enter"
                         :on-click #(rf/dispatch [::ui/finish-polygon])} "close"]
               [:button {:on-click #(rf/dispatch [::ui/cancel-polygon])} "discard"]]
              [:span.dim.hint tip])
       (:brush :eraser) [:<> [size-control]
                         [fit-control @(rf/subscribe [::sub/fit]) ::ui/fit]
                         [:span.dim.hint tip]]
       (if (< 1 (count selections))
         [:span.dim (str (count selections) " selected")]
         [:span.dim.hint tip]))
     [:span.spacer]
     (let [{:keys [on? opacity]} @(rf/subscribe [::render/tracing])]
       [layout/overlay "tracing-controls" "tracing" on?
        "show tracing layers over the picture · never exported"
        #(rf/dispatch [::ui/tracing-on (not on?)])
        (fn [_]
          [:div.view-popout
           [:label "Opacity"
            [:input {:type "range" :min 0 :max 1 :step 0.05 :value opacity
                     :on-change #(rf/dispatch [::ui/tracing-opacity (js/parseFloat (.. % -target -value))])}]]])])
     ;; The stage's zoom and overlays: properties of the view and not of the
     ;; document, so they sit in the view's own chrome rather than in the inspector.
     [layout/stage-controls]]))

(defn adjust-last
  "Blender's Adjust Last Operation, for a brush or eraser stroke: its fit,
  changeable until something else is done."
  []
  (when-let [{:keys [kind ids fit]} @(rf/subscribe [::sub/last])]
    [:div.adjust-last
     [:strong (if (= :eraser kind) "Erase" "Brush stroke")]
     (when (and (= :brush kind) (< 1 (count ids))) [:span.dim (str (count ids) " pieces")])
     [fit-control fit ::ui/adjust-last]]))

